import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  apagarFeriado,
  apagarSerie,
  ateQuandoLegivel,
  buscarFeriados,
  buscarSeries,
  criarFeriado,
  criarSerie,
  gerarMaisSemanas,
  salvarSerie,
  DIAS_DA_SEMANA,
  type Feriado,
  type Serie,
} from '../lib/agenda';
import { useConfirmacao } from './Confirmacao';
import { Alerta, Botao, Cartao } from './Interface';

/**
 * O que se repete toda semana. A serie e um molde: mexer nela vale para o que
 * ainda vai nascer, e o culto ja marcado (com escala e repertorio) fica como
 * esta. A tela diz isso em voz alta, para ninguem esperar outra coisa.
 */
export default function RepeticaoSemanal({ aoMudar }: { aoMudar: () => void }) {
  const [series, setSeries] = useState<Serie[]>([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [abrindo, setAbrindo] = useState(false);

  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [diaFeriado, setDiaFeriado] = useState('');
  const [nomeFeriado, setNomeFeriado] = useState('');

  const [titulo, setTitulo] = useState('');
  const [diaSemana, setDiaSemana] = useState(0);
  const [hora, setHora] = useState('19:00');
  const [local, setLocal] = useState('');

  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const [d, f] = await Promise.all([buscarSeries(), buscarFeriados()]);
      setSeries(d.series);
      setPodeEditar(d.podeEditar);
      setFeriados(f.feriados);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as repeticoes.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function comAviso(acao: () => Promise<string | null>) {
    setOcupado(true);
    setErro(null);
    try {
      const texto = await acao();
      setAviso(texto);
      await carregar();
      aoMudar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  async function criar() {
    if (!titulo.trim()) {
      setErro('De um nome para o que se repete.');
      return;
    }
    await comAviso(async () => {
      const r = await criarSerie({
        titulo: titulo.trim(),
        diaSemana,
        hora,
        local: local.trim() || null,
      });
      setTitulo('');
      setLocal('');
      setAbrindo(false);
      return `${r.gerados} ${r.gerados === 1 ? 'data criada' : 'datas criadas'} na agenda.`;
    });
  }

  return (
    <Cartao>
      {dialogo}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-tinta-900">Repete toda semana</h3>
          <p className="mt-1 text-sm text-tinta-600">
            O culto de domingo e o ensaio entram na agenda sozinhos.
          </p>
        </div>
        {podeEditar && !abrindo && (
          <Botao variante="secundario" className="text-sm" onClick={() => setAbrindo(true)}>
            + Repeticao
          </Botao>
        )}
      </div>

      {erro && (
        <div className="mt-3">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {aviso && (
        <div className="mt-3">
          <Alerta tipo="ok">{aviso}</Alerta>
        </div>
      )}

      {abrindo && podeEditar && (
        <div className="mt-3 space-y-2 rounded-xl bg-tinta-100 p-3">
          <input
            autoFocus
            value={titulo}
            maxLength={191}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Nome (ex.: Culto de domingo)"
            className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <select
              value={diaSemana}
              onChange={(e) => setDiaSemana(Number(e.target.value))}
              className="rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
            >
              {DIAS_DA_SEMANA.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
            <input
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              className="rounded-xl border border-tinta-200 px-3 py-2 text-sm"
            />
            <input
              value={local}
              maxLength={255}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="Local (opcional)"
              className="min-w-[8rem] flex-1 rounded-xl border border-tinta-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Botao carregando={ocupado} onClick={() => void criar()}>
              Criar e preencher 8 semanas
            </Botao>
            <Botao variante="secundario" onClick={() => setAbrindo(false)}>
              Cancelar
            </Botao>
          </div>
        </div>
      )}

      {series.length === 0 ? (
        <p className="mt-3 text-sm text-tinta-400">
          Nada se repete ainda. Cada culto esta sendo criado na mao.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {series.map((s) => (
            <li key={s.id} className={`rounded-xl bg-tinta-100 px-3 py-2 ${s.ativo ? '' : 'opacity-60'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-tinta-900">{s.titulo}</span>
                <span className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-tinta-700">
                  {DIAS_DA_SEMANA[s.diaSemana]} · {s.hora}
                </span>
                {!s.ativo && (
                  <span className="rounded-md bg-tinta-200 px-2 py-0.5 text-xs text-tinta-600">
                    pausada
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-tinta-400">
                {s.proximos} {s.proximos === 1 ? 'data marcada' : 'datas marcadas'}
                {ateQuandoLegivel(s.geradoAte) ? ` · preenchido ate ${ateQuandoLegivel(s.geradoAte)}` : ''}
              </p>

              {podeEditar && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.ativo && (
                    <Botao
                      variante="secundario"
                      className="px-2 py-1 text-xs"
                      onClick={() =>
                        void comAviso(async () => {
                          const r = await gerarMaisSemanas(s.id, 16);
                          return r.gerados === 0
                            ? 'A agenda já estava preenchida até la.'
                            : `${r.gerados} ${r.gerados === 1 ? 'data nova' : 'datas novas'} na agenda.`;
                        })
                      }
                    >
                      Preencher mais 16 semanas
                    </Botao>
                  )}
                  <Botao
                    variante="secundario"
                    className="px-2 py-1 text-xs"
                    onClick={() =>
                      void comAviso(async () => {
                        await salvarSerie(s.id, { ativo: !s.ativo });
                        return s.ativo
                          ? `${s.titulo} pausado. As datas ja marcadas continuam.`
                          : `${s.titulo} reativado.`;
                      })
                    }
                  >
                    {s.ativo ? 'Pausar' : 'Reativar'}
                  </Botao>
                  <button
                    onClick={async () => {
                      const ok = await confirmar({
                        titulo: 'Apagar a repeticao?',
                        texto: `As ${s.proximos} datas ja marcadas continuam na agenda, com escala e repertorio. Só para de criar datas novas.`,
                        confirmar: 'Apagar',
                      });
                      if (!ok) return;
                      void comAviso(async () => {
                        const r = await apagarSerie(s.id);
                        return `Repeticao apagada. ${r.eventosMantidos} ${
                          r.eventosMantidos === 1 ? 'data mantida' : 'datas mantidas'
                        } na agenda.`;
                      });
                    }}
                    className="px-2 py-1 text-xs text-tinta-400 hover:text-red-600"
                  >
                    apagar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 border-t border-tinta-200 pt-4">
        <h4 className="text-sm font-semibold text-tinta-900">Dias sem culto</h4>
        <p className="mt-1 text-xs text-tinta-400">
          Feriado e recesso. A repeticao pula esses dias — o que ja estava marcado continua.
        </p>

        {podeEditar && (
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              type="date"
              value={diaFeriado}
              onChange={(e) => setDiaFeriado(e.target.value)}
              className="rounded-xl border border-tinta-200 px-3 py-1.5 text-sm"
            />
            <input
              value={nomeFeriado}
              maxLength={191}
              onChange={(e) => setNomeFeriado(e.target.value)}
              placeholder="Motivo (ex.: Natal)"
              className="min-w-[8rem] flex-1 rounded-xl border border-tinta-200 px-3 py-1.5 text-sm"
            />
            <Botao
              variante="secundario"
              className="px-3 py-1.5 text-sm"
              carregando={ocupado}
              onClick={() => {
                if (!diaFeriado || !nomeFeriado.trim()) {
                  setErro('Escolha o dia e diga o motivo.');
                  return;
                }
                void comAviso(async () => {
                  const r = await criarFeriado({ dia: diaFeriado, nome: nomeFeriado.trim() });
                  setDiaFeriado('');
                  setNomeFeriado('');
                  return r.eventosNesseDia > 0
                    ? `Dia marcado. Ja havia ${r.eventosNesseDia} evento(s) nesse dia — cancele na agenda se for o caso.`
                    : 'Dia marcado.';
                });
              }}
            >
              Marcar
            </Botao>
          </div>
        )}

        {feriados.length === 0 ? (
          <p className="mt-2 text-xs text-tinta-400">Nenhum dia marcado.</p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-2">
            {feriados.map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-2 rounded-lg bg-tinta-100 px-2 py-1 text-xs"
              >
                <span className="font-medium text-tinta-800">{ateQuandoLegivel(f.dia)}</span>
                <span className="text-tinta-400">{f.nome}</span>
                {podeEditar && (
                  <button
                    onClick={() =>
                      void comAviso(async () => {
                        await apagarFeriado(f.id);
                        return 'Dia liberado.';
                      })
                    }
                    className="text-tinta-400 hover:text-red-600"
                    aria-label={`Liberar ${f.nome}`}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Cartao>
  );
}
